// Kartu prestasi yang bisa dibagikan: gambar PNG 1080×1350 (TAKA berkostum, nama panggilan, level,
// streak, XP, lencana terbaik) digambar di <canvas>, lalu dibagikan lewat Web Share API
// (WhatsApp, dsb.) atau disimpan sebagai berkas bila perangkat tidak mendukung berbagi berkas.
import { $ } from '../core/dom.js';
import { esc, fmt } from '../core/format.js';
import { S } from '../app/state.js';
import { mascot, GAYA_MASKOT } from '../app/mascot.js';
import { sfx } from '../app/sound.js';
import { ringkas } from './profil.js';
import { TINGKAT_LENCANA } from './aturan.js';

const W = 1080, H = 1350;
const WARNA = { ink: '#22244A', soft: '#575C8C', grape: '#8B5CF6', sky: '#2F9BFF', sun: '#FFC531', sunSoft: '#FFF1C7', pink: '#FF5C8D', mint: '#1FBF89', tile: '#F3F6FF', line: '#D3DAF3' };
const TINGKAT_WARNA = ['#D3DAF3', '#E3A06A', '#C9D2DE', '#FFD54A'];
const EMOJI = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
const DISPLAY = 'Fredoka,"Baloo 2","Trebuchet MS",sans-serif';
const BODY = 'Nunito,"Segoe UI",sans-serif';
// Pose sayap statis (di aplikasi diatur overlay.css, yang tidak ikut ke gambar SVG terpisah).
const POSE = '.m-wl{transform-box:view-box;transform-origin:27px 64px}.m-wr{transform-box:view-box;transform-origin:93px 64px}.m-up-l{transform:rotate(112deg)}.m-up-r{transform:rotate(-112deg)}.m-wave{transform:rotate(-60deg)}';

const namaPanggil = () => (S.nama || 'Aku').trim().split(/\s+/)[0].slice(0, 20);
const situs = () => location.host;

function kotak(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
function teks(ctx, isi, x, y, { font, warna = WARNA.ink, rata = 'center', maks } = {}) {
  ctx.font = font; ctx.fillStyle = warna; ctx.textAlign = rata; ctx.textBaseline = 'alphabetic';
  if (maks) { // perkecil huruf sampai muat
    let ukuran = Number(/(\d+)px/.exec(font)[1]);
    while (ctx.measureText(isi).width > maks && ukuran > 20) { ukuran -= 2; ctx.font = font.replace(/\d+px/, `${ukuran}px`); }
  }
  ctx.fillText(isi, x, y);
}
async function gambarMaskot(kostum) {
  const svg = mascot('cheer', 400, '', { kostum })
    .replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ')
    .replace('viewBox="0 0 120 120"', 'viewBox="-4 -12 128 128"')
    .replace('<g class="m-all">', `<style>${GAYA_MASKOT}${POSE}</style><g class="m-all">`);
  const img = new Image();
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  await img.decode();
  return img;
}
async function siapkanHuruf() {
  if (!document.fonts?.load) return;
  await Promise.all(['700 80px Fredoka', '600 34px Fredoka', '800 44px Nunito', '700 28px Nunito'].map(f => document.fonts.load(f).catch(() => {})));
}

/**
 * Data kartu dari profil anak. `sorot` (opsional) menonjolkan pencapaian baru:
 * { jenis: 'level' } atau { jenis: 'lencana', lencana, tingkat }.
 */
export function dataKartu({ sorot } = {}) {
  const r = ringkas(), didapat = r.lencana.filter(l => l.tingkat).sort((a, b) => b.tingkat - a.tingkat);
  let lencana = didapat;
  if (sorot?.jenis === 'lencana') lencana = [{ ...sorot.lencana, tingkat: sorot.tingkat }, ...didapat.filter(l => l.id !== sorot.lencana.id)];
  const label = sorot?.jenis === 'level' ? 'Naik level!' : sorot?.jenis === 'lencana' ? `Lencana baru · ${TINGKAT_LENCANA[sorot.tingkat]}` : 'Prestasiku';
  const sub = sorot?.jenis === 'lencana' ? `${sorot.lencana.ikon} ${sorot.lencana.nama}` : `Level ${r.level.level} · ${r.level.gelar}`;
  return {
    nama: namaPanggil(), label, sub, kostum: r.p.kostum,
    level: r.level.level, xp: r.xp, streak: r.streak.streak, jumlahLencana: didapat.length, totalLencana: r.lencana.length,
    lencana: lencana.slice(0, 6).map(l => ({ ikon: l.ikon, tingkat: l.tingkat })),
  };
}

/** Gambar kartu ke canvas baru. */
export async function gambarKartu(d) {
  await siapkanHuruf();
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');

  // latar: gradasi langit ke ungu, taburan konfeti
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, WARNA.sky); g.addColorStop(1, WARNA.grape);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  const KONFETI = [[90, 70, WARNA.sun], [990, 120, WARNA.pink], [40, 600, WARNA.mint], [1040, 520, WARNA.sun], [70, 1230, WARNA.pink], [1010, 1270, WARNA.mint], [930, 40, '#FFFFFF'], [160, 1300, WARNA.sun]];
  KONFETI.forEach(([x, y, c], i) => { ctx.globalAlpha = 0.85; ctx.fillStyle = c; ctx.beginPath(); ctx.arc(x, y, 14 + (i % 3) * 8, 0, Math.PI * 2); ctx.fill(); });
  ctx.globalAlpha = 1;

  teks(ctx, 'Kuis Juara TKA', W / 2, 118, { font: `700 56px ${DISPLAY}`, warna: '#FFFFFF' });

  // kartu putih bergaya "chunky" seperti aplikasi
  const kx = 60, ky = 170, kw = W - 120, kh = 1010;
  ctx.fillStyle = WARNA.ink; kotak(ctx, kx, ky + 14, kw, kh, 56); ctx.fill();
  ctx.fillStyle = '#FFFFFF'; kotak(ctx, kx, ky, kw, kh, 56); ctx.fill();
  ctx.lineWidth = 6; ctx.strokeStyle = WARNA.ink; ctx.stroke();

  // TAKA di atas lingkaran matahari
  ctx.fillStyle = WARNA.sunSoft; ctx.beginPath(); ctx.arc(W / 2, 410, 205, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = WARNA.sun; ctx.globalAlpha = 0.35; ctx.beginPath(); ctx.arc(W / 2, 410, 160, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1;
  try { ctx.drawImage(await gambarMaskot(d.kostum), W / 2 - 200, 200, 400, 400); } catch (e) { /* tanpa maskot pun kartu tetap jadi */ }

  teks(ctx, d.label.toUpperCase(), W / 2, 668, { font: `600 34px ${DISPLAY}`, warna: WARNA.soft, maks: kw - 80 });
  teks(ctx, d.nama, W / 2, 752, { font: `700 84px ${DISPLAY}`, maks: kw - 80 });
  teks(ctx, d.sub, W / 2, 818, { font: `800 44px ${BODY}`, warna: WARNA.grape, maks: kw - 80 });

  // tiga ubin angka
  const ubin = [['🔥', fmt(d.streak), 'hari beruntun'], ['⭐', fmt(d.xp), `XP · Level ${d.level}`], ['🏅', `${d.jumlahLencana}/${d.totalLencana}`, 'lencana']];
  const uw = 270, ug = 25, ux = (W - (uw * 3 + ug * 2)) / 2, uy = 862, uh = 170;
  ubin.forEach(([ikon, nilai, ket], i) => {
    const x = ux + i * (uw + ug), cx = x + uw / 2;
    ctx.fillStyle = WARNA.tile; kotak(ctx, x, uy, uw, uh, 28); ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = WARNA.line; ctx.stroke();
    teks(ctx, ikon, cx, uy + 56, { font: `40px ${EMOJI}` });
    teks(ctx, nilai, cx, uy + 116, { font: `700 52px ${DISPLAY}`, maks: uw - 30 });
    teks(ctx, ket, cx, uy + 152, { font: `700 26px ${BODY}`, warna: WARNA.soft, maks: uw - 30 });
  });

  // lencana terbaik
  const ly = 1102;
  if (d.lencana.length) {
    const r = 44, jarak = 112, x0 = W / 2 - ((d.lencana.length - 1) * jarak) / 2;
    d.lencana.forEach((l, i) => {
      const x = x0 + i * jarak;
      ctx.fillStyle = TINGKAT_WARNA[l.tingkat] || TINGKAT_WARNA[0]; ctx.beginPath(); ctx.arc(x, ly, r, 0, Math.PI * 2); ctx.fill();
      ctx.lineWidth = 4; ctx.strokeStyle = WARNA.ink; ctx.stroke();
      ctx.textBaseline = 'middle'; ctx.font = `44px ${EMOJI}`; ctx.textAlign = 'center'; ctx.fillStyle = WARNA.ink; ctx.fillText(l.ikon, x, ly + 3);
    });
  } else {
    teks(ctx, 'Lencana pertamaku sedang menunggu!', W / 2, ly + 12, { font: `700 34px ${BODY}`, warna: WARNA.soft });
  }

  teks(ctx, 'Ayo latihan TKA SD bareng TAKA!', W / 2, 1258, { font: `800 44px ${BODY}`, warna: '#FFFFFF', maks: W - 120 });
  teks(ctx, situs(), W / 2, 1310, { font: `700 34px ${BODY}`, warna: '#FFFFFFCC' });
  return cv;
}

const keBlob = cv => new Promise((ok, gagal) => cv.toBlob(b => (b ? ok(b) : gagal(new Error('Gambar gagal dibuat.'))), 'image/png'));
const namaBerkas = nama => `prestasi-${nama.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'taka'}.png`;
export const teksBagikan = d => `${d.label === 'Naik level!' ? `Aku naik ke Level ${d.level}` : `Aku sudah Level ${d.level}`} di Kuis Juara TKA! Ayo latihan bareng TAKA: https://${situs()}`;

/** Tampilkan pratinjau kartu dengan tombol Bagikan / Simpan gambar. */
export async function bukaKartu(opsi = {}) {
  sfx.tap?.();
  $('.kb-lapis')?.remove();
  const el = document.createElement('div');
  el.className = 'kb-lapis';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', 'Bagikan kartu prestasi');
  el.innerHTML = '<div class="card kb-kartu"><p class="muted">Membuat kartu prestasi…</p></div>';
  document.body.append(el);
  const kartu = $('.kb-kartu', el);
  let url = '';
  const tutup = () => { el.remove(); document.removeEventListener('keydown', tombolEsc); if (url) URL.revokeObjectURL(url); };
  const tombolEsc = e => { if (e.key === 'Escape') tutup(); };
  document.addEventListener('keydown', tombolEsc);
  el.addEventListener('click', e => { if (e.target === el) tutup(); });
  try {
    const d = dataKartu(opsi), blob = await keBlob(await gambarKartu(d));
    const berkas = new File([blob], namaBerkas(d.nama), { type: 'image/png' });
    url = URL.createObjectURL(blob);
    const bisaBagi = !!navigator.canShare?.({ files: [berkas] });
    kartu.innerHTML = `<img class="kb-gambar" src="${url}" alt="Kartu prestasi ${esc(d.nama)}: ${esc(d.sub)}, ${d.streak} hari beruntun, ${fmt(d.xp)} XP">
      <div class="kb-aksi">${bisaBagi ? '<button class="btn btn-go" type="button" data-bagi>📤 Bagikan</button>' : ''}
        <a class="btn${bisaBagi ? ' btn-sm' : ' btn-go'}" href="${url}" download="${esc(berkas.name)}" data-simpan>⬇️ Simpan gambar</a>
        <button class="btn btn-sm btn-ghost" type="button" data-tutup>Tutup</button></div>
      <p class="muted kecil">Minta izin ayah, ibu, atau guru sebelum membagikan, ya.</p>`;
    $('[data-bagi]', kartu)?.addEventListener('click', async () => {
      try { await navigator.share({ files: [berkas], title: 'Prestasiku di Kuis Juara TKA', text: teksBagikan(d) }); } catch (e) { /* dibatalkan */ }
    });
    $('[data-simpan]', kartu).addEventListener('click', () => sfx.tap?.());
    $('[data-tutup]', kartu).onclick = tutup;
    ($('[data-bagi]', kartu) || $('[data-simpan]', kartu)).focus();
  } catch (e) {
    kartu.innerHTML = `<p>Kartu belum bisa dibuat di perangkat ini.</p><button class="btn" type="button" data-tutup>Tutup</button>`;
    $('[data-tutup]', kartu).onclick = tutup;
  }
}
